import type { FastifyRequest, FastifyReply } from 'fastify'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import type {
  CreateShareDTO,
  ShareLink,
  Comment,
  CreateCommentDTO,
  AccessShareDTO,
  PublicPlanResponse
} from '@my-roadtrip/types'
import { CollabService } from './collab.service.js'
import { SharingService } from './sharing.service.js'
import { ApiResponse } from '../../utils/response.js'
import { createLogger } from '../../common/utils/logger.js'
import { emitPlanShared, emitPlanShareRemoved } from '../../core/events/eventEmitter.js'

const logger = createLogger('CollabController')

export class CollabController {
  private readonly service: CollabService
  private readonly sharingService: SharingService

  constructor(
    private readonly redis: RedisClient,
    private readonly firestore: Firestore
  ) {
    this.service = new CollabService(redis, firestore)
    this.sharingService = new SharingService(firestore, redis)
  }

  async sync(request: FastifyRequest<{ Params: { planId: string } }>, reply: FastifyReply) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      const deviceId = request.headers['x-device-id'] as string
      const startTime = Date.now()
      logger.debug('CRDT sync request received', { planId, userId, deviceId })

      if (!userId) {
        logger.warn('CRDT sync blocked: missing user', { planId })
        return ApiResponse.unauthorized(reply)
      }

      // ✅ SECURITY FIX #1: Validate plan ownership with cached metadata
      const planAccess = await this.service.getPlanAccess(planId)
      if (!planAccess) {
        logger.warn('CRDT sync failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const isOwner = planAccess.ownerId === userId
      const isCollaborator = planAccess.collaborators.some(
        collaborator => collaborator.userId === userId && (collaborator.role === 'editor' || collaborator.role === 'owner')
      )

      if (!isOwner && !isCollaborator) {
        logger.warn('CRDT sync forbidden: missing permissions', { planId, userId })
        return ApiResponse.forbidden(reply, 'Not authorized to edit this plan')
      }

      // Read binary body (already parsed by content type parser)
      const clientUpdate = request.body as Buffer

      // Merge with server state
      const mergedState = await this.service.syncPlan(
        planId,
        userId,
        deviceId,
        clientUpdate,
        planAccess.ownerId
      )
      logger.info('CRDT sync completed', {
        planId,
        userId,
        deviceId,
        responseSize: mergedState.length,
        durationMs: Date.now() - startTime
      })

      // Return binary response
      return reply.header('Content-Type', 'application/octet-stream').send(mergedState)

    } catch (error) {
      logger.error('CRDT sync failed', { error, planId: request.params.planId, userId: request.user?.uid })
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Sync failed')
    }
  }

  async getPresence(request: FastifyRequest<{ Params: { planId: string } }>, reply: FastifyReply) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      logger.debug('Presence request received', { planId, userId })

      // ✅ SECURITY: Validate plan access for presence
      const planAccess = await this.service.getPlanAccess(planId)
      if (!planAccess) {
        logger.warn('Presence lookup failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      // Require authentication for private plans
      if (!planAccess.isPublic && !userId) {
        logger.warn('Presence access forbidden: unauthenticated user for private plan', { planId })
        return ApiResponse.unauthorized(reply)
      }

      if (userId) {
        const hasAccess =
          planAccess.ownerId === userId ||
          planAccess.isPublic ||
          planAccess.collaborators.some(collaborator => collaborator.userId === userId)

        if (!hasAccess) {
          logger.warn('Presence access forbidden', { planId, userId })
          return ApiResponse.forbidden(reply, 'Not authorized to view this plan')
        }
      }

      const presence = await this.service.getPresence(planId)
      logger.info('Presence data returned', { planId, count: presence.length })
      return reply.send(presence)
    } catch (error) {
      logger.error('Failed to load presence data', { error, planId: request.params.planId, userId: request.user?.uid })
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to get presence')
    }
  }

  async updatePresence(
    request: FastifyRequest<{
      Params: { planId: string }
      Body: { cursor?: { lat: number; lng: number } | { x: number; y: number }; deviceId?: string; timestamp?: number }
    }>,
    reply: FastifyReply
  ) {
    const { planId } = request.params
    try {
      const userId = request.user?.uid
      logger.debug('Update presence request', { planId, userId })

      if (!userId) {
        logger.warn('Presence update denied: missing user', { planId })
        return ApiResponse.unauthorized(reply)
      }

      // ✅ SECURITY: Validate plan access before updating presence
      const planAccess = await this.service.getPlanAccess(planId)
      if (!planAccess) {
        logger.warn('Presence update failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const hasAccess =
        planAccess.ownerId === userId ||
        planAccess.isPublic ||
        planAccess.collaborators.some(collaborator => collaborator.userId === userId)

      if (!hasAccess) {
        logger.warn('Presence update forbidden', { planId, userId })
        return ApiResponse.forbidden(reply, 'Not authorized to view this plan')
      }

      const { cursor, deviceId, timestamp } = request.body || {}
      const normalizedCursor =
        cursor && 'lat' in cursor && 'lng' in cursor
          ? { lat: cursor.lat, lng: cursor.lng }
          : undefined

      await this.service.updatePresence(planId, userId, {
        cursor: normalizedCursor,
        deviceId: deviceId || 'unknown',
        timestamp: timestamp || Date.now()
      })
      logger.info('Presence updated', {
        planId,
        userId,
        hasCursor: Boolean(normalizedCursor),
        deviceId: deviceId || 'unknown'
      })
      return reply.send({ success: true })
    } catch (error) {
      logger.error('Failed to update presence', { error, planId, userId: request.user?.uid })
      request.log.error(error)
      return ApiResponse.internalError(reply, 'Failed to update presence')
    }
  }

  // ===== SHARING METHODS (merged from sharing.controller) =====

  async createShare(
    request: FastifyRequest,
    reply: FastifyReply,
    planId: string,
    data: CreateShareDTO
  ): Promise<ShareLink | void> {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.info('Create share requested', {
        planId,
        userId,
        expiresAt: data.expiresAt ?? null,
        passwordProtected: Boolean(data.password)
      })

      // Validate ownership
      const planDoc = await this.service.getPlanDocument(planId)
      if (!planDoc.exists) {
        logger.warn('Create share failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const planData = planDoc.data()
      const isOwner = planData?.userId === userId
      const isEditor =
        Array.isArray(planData?.collaborators) &&
        planData.collaborators.some(
          (collaborator: { userId?: string; role?: string }) =>
            collaborator?.userId === userId && ['editor', 'owner'].includes(collaborator?.role ?? '')
        )

      if (!isOwner && !isEditor) {
        logger.warn('Create share forbidden: missing rights', { planId, userId })
        return ApiResponse.forbidden(reply, 'Not authorized to share this plan')
      }

      const share = await this.sharingService.createShare(planId, userId, data)
      emitPlanShared({
        planId,
        ownerId: userId,
        recipientId: share.id,
        shareType: 'view',
        timestamp: Date.now()
      })
      logger.info('Share link created', { planId, userId, shareId: share.id })
      return ApiResponse.success(reply, share, 201)
    } catch (error) {
      logger.error('Create share failed', { error: (error as Error).message, planId })
      return ApiResponse.internalError(reply, 'Failed to create share')
    }
  }

  async listShares(
    request: FastifyRequest,
    reply: FastifyReply,
    planId: string
  ): Promise<ShareLink[] | void> {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('List shares requested', { planId, userId })

      // Validate ownership
      const planDoc = await this.service.getPlanDocument(planId)
      if (!planDoc.exists) {
        logger.warn('List shares failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const planData = planDoc.data()
      if (planData?.userId !== userId) {
        logger.warn('List shares forbidden', { planId, userId })
        return ApiResponse.forbidden(reply, 'Not authorized to list shares')
      }

      const shares = await this.sharingService.listShares(planId, userId)
      logger.info('Shares listed', { planId, userId, count: shares.length })
      return ApiResponse.success(reply, shares)
    } catch (error) {
      logger.error('List shares failed', { error: (error as Error).message, planId })
      return ApiResponse.internalError(reply, 'Failed to list shares')
    }
  }

  async accessShare(
    request: FastifyRequest,
    reply: FastifyReply,
    token: string,
    data: AccessShareDTO
  ): Promise<ShareLink | void> {
    try {
      logger.debug('Access share requested', {
        tokenPreview: token.slice(0, 8),
        hasPassword: Boolean(data.password)
      })
      const share = await this.sharingService.accessShare(token, data.password)
      logger.info('Share accessed successfully', { tokenPreview: token.slice(0, 8) })
      return ApiResponse.success(reply, share)
    } catch (error: any) {
      logger.error('Access share failed', { error: error.message, token: token.substring(0, 10) })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }

  async deleteShare(
    request: FastifyRequest,
    reply: FastifyReply,
    shareId: string
  ): Promise<void> {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Delete share requested', { shareId, userId })

      const share = await this.sharingService.getShareById(shareId)
      if (!share) {
        logger.warn('Delete share failed: not found', { shareId })
        return ApiResponse.notFound(reply, 'Share not found')
      }

      // Validate ownership
      const planDoc = await this.service.getPlanDocument(share.planId)
      if (!planDoc.exists) {
        logger.warn('Delete share failed: plan missing', { shareId, planId: share.planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const planData = planDoc.data()
      if (planData?.userId !== userId && share.createdBy !== userId) {
        logger.warn('Delete share forbidden', { shareId, userId })
        return ApiResponse.forbidden(reply, 'Not authorized to delete this share')
      }

      await this.sharingService.deleteShare(shareId)
      emitPlanShareRemoved({
        planId: share.planId,
        ownerId: share.createdBy ?? (planData?.userId as string),
        shareId,
        timestamp: Date.now()
      })
      logger.info('Share deleted', { shareId, userId })
      return ApiResponse.noContent(reply)
    } catch (error) {
      logger.error('Delete share failed', { error: (error as Error).message, shareId })
      return ApiResponse.internalError(reply, 'Failed to delete share')
    }
  }

  /**
   * Join a shared plan - adds user as collaborator on the ORIGINAL plan
   */
  async joinPlan(
    request: FastifyRequest,
    reply: FastifyReply,
    token: string,
    data: { password?: string }
  ): Promise<{ planId: string; permission: 'view' | 'edit' } | void> {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      logger.info('Join plan requested', {
        tokenPreview: token.slice(0, 8),
        userId,
        hasPassword: Boolean(data.password)
      })

      const result = await this.sharingService.joinPlan(token, userId, data.password)

      logger.info('User joined plan', {
        planId: result.planId,
        userId,
        permission: result.permission
      })

      return ApiResponse.success(reply, result)
    } catch (error: any) {
      logger.error('Join plan failed', { error: error.message, token: token.substring(0, 10) })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }

  async getComments(
    request: FastifyRequest,
    reply: FastifyReply,
    planId: string
  ): Promise<Comment[] | void> {
    try {
      const userId = request.user?.uid
      const planAccess = await this.service.getPlanAccess(planId)
      if (!planAccess) {
        logger.warn('Get comments failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const isOwner = userId && planAccess.ownerId === userId
      const isCollaborator = userId
        ? planAccess.collaborators.some(collaborator => collaborator.userId === userId)
        : false

      if (!isOwner && !isCollaborator) {
        const commentsEnabled = await this.sharingService.areCommentsEnabled(planId)
        if (!commentsEnabled) {
          logger.warn('Comments access blocked: disabled for plan', { planId, userId: userId ?? 'anonymous' })
          return ApiResponse.forbidden(reply, 'Comments are disabled for this plan')
        }
      }

      logger.debug('Get comments requested', { planId })
      const comments = await this.sharingService.getComments(planId)
      logger.info('Comments retrieved', { planId, count: comments.length })
      return ApiResponse.success(reply, comments)
    } catch (error) {
      logger.error('Get comments failed', { error: (error as Error).message, planId })
      return ApiResponse.internalError(reply, 'Failed to get comments')
    }
  }

  async createComment(
    request: FastifyRequest,
    reply: FastifyReply,
    planId: string,
    data: CreateCommentDTO
  ): Promise<Comment | void> {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      const planAccess = await this.service.getPlanAccess(planId)
      if (!planAccess) {
        logger.warn('Create comment failed: plan not found', { planId })
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const isOwner = planAccess.ownerId === userId
      const isCollaborator = planAccess.collaborators.some(collaborator => collaborator.userId === userId)
      const commentsEnabled = isOwner || isCollaborator || await this.sharingService.areCommentsEnabled(planId)
      if (!commentsEnabled) {
        logger.warn('Create comment forbidden: comments disabled', { planId, userId })
        return ApiResponse.forbidden(reply, 'Comments are disabled for this plan')
      }

      logger.debug('Create comment requested', { planId, userId })

      const comment = await this.sharingService.createComment(planId, userId, data)
      logger.info('Comment created', { planId, userId, commentId: comment.id })
      return ApiResponse.success(reply, comment, 201)
    } catch (error) {
      logger.error('Create comment failed', { error: (error as Error).message, planId })
      return ApiResponse.internalError(reply, 'Failed to create comment')
    }
  }

  async deleteComment(
    request: FastifyRequest,
    reply: FastifyReply,
    commentId: string
  ): Promise<void> {
    try {
      const userId = request.user?.uid
      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Delete comment requested', { commentId, userId })

      const comment = await this.sharingService.getCommentById(commentId)
      if (!comment) {
        logger.warn('Delete comment failed: not found', { commentId })
        return ApiResponse.notFound(reply, 'Comment not found')
      }

      if (comment.userId !== userId) {
        logger.warn('Delete comment forbidden', { commentId, userId })
        return ApiResponse.forbidden(reply, 'Not authorized to delete this comment')
      }

      await this.sharingService.deleteComment(commentId)
      logger.info('Comment deleted', { commentId, userId })
      return ApiResponse.noContent(reply)
    } catch (error) {
      logger.error('Delete comment failed', { error: (error as Error).message, commentId })
      return ApiResponse.internalError(reply, 'Failed to delete comment')
    }
  }

  async getPublicPlan(
    request: FastifyRequest,
    reply: FastifyReply,
    token: string,
    data: { password?: string }
  ): Promise<PublicPlanResponse | void> {
    try {
      logger.debug('Get public plan requested', { tokenPreview: token.slice(0, 8) })
      const result = await this.sharingService.getPublicPlan(token, data.password)
      logger.info('Public plan resolved from token', { tokenPreview: token.slice(0, 8), hasPassword: Boolean(data.password) })
      return ApiResponse.success(reply, result)
    } catch (error: any) {
      logger.error('Get public plan failed', { error: error.message, token: token.substring(0, 10) })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }

  // ===== COLLABORATOR MANAGEMENT =====

  async getCollaborators(
    request: FastifyRequest<{ Params: { planId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      logger.debug('Get collaborators requested', { planId, userId })

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      // Check access
      const planAccess = await this.service.getPlanAccess(planId)
      if (!planAccess) {
        return ApiResponse.notFound(reply, 'Plan not found')
      }

      const hasAccess =
        planAccess.ownerId === userId ||
        planAccess.collaborators.some(c => c.userId === userId)

      if (!hasAccess) {
        return ApiResponse.forbidden(reply, 'Not authorized to view this plan')
      }

      const collaborators = await this.sharingService.getCollaborators(planId)

      // Enrich with presence data
      const presence = await this.service.getPresence(planId)
      const presenceMap = new Map(presence.map(p => [p.userId, p]))

      const enriched = collaborators.map(c => ({
        ...c,
        status: presenceMap.has(c.userId) ? 'active' : 'offline',
        lastSeen: presenceMap.get(c.userId)?.timestamp || c.lastSeen
      }))

      // Get owner profile
      const ownerProfile = planAccess.ownerId
        ? await this.sharingService.getOwnerProfile(planAccess.ownerId)
        : null

      // Add owner with enriched data
      const ownerPresence = presenceMap.get(planAccess.ownerId || '')
      const result = {
        owner: {
          userId: planAccess.ownerId,
          role: 'owner',
          displayName: ownerProfile?.displayName || null,
          email: ownerProfile?.email || null,
          iconSet: ownerProfile?.iconSet || null,
          status: ownerPresence ? 'active' : 'offline',
          lastSeen: ownerPresence?.timestamp
        },
        collaborators: enriched
      }

      logger.debug('Collaborators response', { planId, collaboratorCount: enriched.length })
      return reply.send(result)
    } catch (error) {
      logger.error('Get collaborators failed', { error, planId: request.params.planId })
      return ApiResponse.internalError(reply, 'Failed to get collaborators')
    }
  }

  async updateCollaboratorRole(
    request: FastifyRequest<{
      Params: { planId: string; collaboratorId: string }
      Body: { role: 'admin' | 'editor' | 'viewer' }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { planId, collaboratorId } = request.params
      const { role } = request.body
      const userId = request.user?.uid
      logger.debug('Update collaborator role requested', { planId, collaboratorId, role, userId })

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      if (!['admin', 'editor', 'viewer'].includes(role)) {
        return ApiResponse.badRequest(reply, 'Invalid role')
      }

      await this.sharingService.updateCollaboratorRole(planId, userId, collaboratorId, role)

      // Invalidate cache
      await this.service.invalidatePlanAccess(planId)

      logger.info('Collaborator role updated', { planId, collaboratorId, role })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Update collaborator role failed', { error: error.message, planId: request.params.planId })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }

  async removeCollaborator(
    request: FastifyRequest<{
      Params: { planId: string; collaboratorId: string }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { planId, collaboratorId } = request.params
      const userId = request.user?.uid
      logger.debug('Remove collaborator requested', { planId, collaboratorId, userId })

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      await this.sharingService.removeCollaborator(planId, userId, collaboratorId)

      // Invalidate cache
      await this.service.invalidatePlanAccess(planId)

      logger.info('Collaborator removed', { planId, collaboratorId })
      return ApiResponse.noContent(reply)
    } catch (error: any) {
      logger.error('Remove collaborator failed', { error: error.message, planId: request.params.planId })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }

  async updateCollaboratorPermissions(
    request: FastifyRequest<{
      Params: { planId: string; collaboratorId: string }
      Body: { permissions: Record<string, boolean> }
    }>,
    reply: FastifyReply
  ) {
    try {
      const { planId, collaboratorId } = request.params
      const { permissions } = request.body
      const userId = request.user?.uid
      logger.debug('Update collaborator permissions requested', { planId, collaboratorId, userId })

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      if (!permissions || typeof permissions !== 'object') {
        return ApiResponse.badRequest(reply, 'Invalid permissions')
      }

      await this.sharingService.updateCollaboratorPermissions(planId, userId, collaboratorId, permissions)

      // Invalidate cache
      await this.service.invalidatePlanAccess(planId)

      logger.info('Collaborator permissions updated', { planId, collaboratorId })
      return reply.send({ success: true })
    } catch (error: any) {
      logger.error('Update collaborator permissions failed', { error: error.message, planId: request.params.planId })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }

  async leavePlan(
    request: FastifyRequest<{ Params: { planId: string } }>,
    reply: FastifyReply
  ) {
    try {
      const { planId } = request.params
      const userId = request.user?.uid
      logger.debug('Leave plan requested', { planId, userId })

      if (!userId) {
        return ApiResponse.unauthorized(reply)
      }

      await this.sharingService.removeCollaborator(planId, userId, userId)

      // Invalidate cache
      await this.service.invalidatePlanAccess(planId)

      logger.info('User left plan', { planId, userId })
      return ApiResponse.noContent(reply)
    } catch (error: any) {
      logger.error('Leave plan failed', { error: error.message, planId: request.params.planId })
      const status = error.statusCode || 500
      return ApiResponse.error(reply, status, error.message)
    }
  }
}
